<template>
  <header class="header-two-eight" :class="{ scrolled: isScrolled }">
    <div class="header-two-eight-inner">
      <!-- Logo — tenant asset, always from the header config. -->
      <NuxtLink class="logo" :to="localePath('/')" aria-label="home">
        <NuxtImg
          loading="eager"
          v-no-right-click
          :src="headerData.logo"
          :alt="config.public.name"
        />
      </NuxtLink>

      <!-- Primary links. Driven by `header.links` so a client can list as many
           or as few as it likes; the row simply shrinks. -->
      <nav v-if="links.length" class="nav-links desktop-only">
        <NuxtLink
          v-for="link in links"
          :key="link.target"
          :to="localePath(link.target)"
          class="nav-link"
        >
          {{ t(link.title) }}
        </NuxtLink>
      </nav>

      <div class="header-actions">
        <Cart class="cart desktop-only" @open-login="emit('open-login', $event)" />

        <TheHeaderSwitcher
          v-if="headerData.isMultiLanguage && hasMultiLang"
          class="desktop-only"
          :active-item="activeLang"
          :show-code="false"
          :list="availableLocales"
          :header-data="headerData"
          is-centered
          :route-path="routePath"
          @update:active-item="switchLanguage"
        />

        <TheHeaderSwitcher
          v-if="headerData.isMultiCurrency && hasMultiCurrency"
          class="desktop-only"
          :list="simplifiedCurrencies"
          :active-item="activeCurrency || ''"
          :header-data="headerData"
          :route-path="routePath"
          @update:active-item="switchCurrency"
        />

        <a
          v-if="headerData.phone"
          class="phone-pill desktop-only"
          :href="`tel:${headerData.phone}`"
        >
          <icon-phone-in-call />
          <span dir="ltr">{{ headerData.phone }}</span>
        </a>

        <button
          class="menu-control"
          aria-label="toggle menu"
          @click="menuOpen = true"
        >
          <icon-hamburger />
        </button>
      </div>
    </div>

    <BaseBottomSheet
      v-model:show="menuOpen"
      full-height
      background-color="bg-white"
    >
      <template #header>
        <NuxtLink
          class="sheet-logo"
          :to="localePath('/')"
          aria-label="home"
          @click="menuOpen = false"
        >
          <NuxtImg
            loading="lazy"
            v-no-right-click
            :src="headerData.logo"
            :alt="config.public.name"
          />
        </NuxtLink>
      </template>

      <div class="mobile-menu">
        <!-- Pages: one per row, each with the house arrow, so a tap target
             spans the full width rather than just the words. -->
        <nav v-if="links.length" class="mobile-nav">
          <NuxtLink
            v-for="link in links"
            :key="link.target"
            :to="localePath(link.target)"
            class="mobile-link"
            @click="menuOpen = false"
          >
            <span>{{ t(link.title) }}</span>
            <icon-arrow-right class="mobile-link-arrow" />
          </NuxtLink>

          <NuxtLink
            :to="localePath({ name: 'cart' })"
            class="mobile-link"
            @click="menuOpen = false"
          >
            <span class="mobile-link-cart">
              {{ t("paymentCart.shoppingCart") }}
              <span v-if="cartCount" class="cart-count">{{ cartCount }}</span>
            </span>
            <icon-arrow-right class="mobile-link-arrow" />
          </NuxtLink>
        </nav>

        <!-- Preferences sit together, below the pages. -->
        <div
          v-if="
            (headerData.isMultiLanguage && hasMultiLang) ||
            (headerData.isMultiCurrency && hasMultiCurrency)
          "
          class="mobile-switchers"
        >
          <TheHeaderSwitcher
            v-if="headerData.isMultiLanguage && hasMultiLang"
            show-bottom-sheet
            :active-item="activeLang"
            :show-code="false"
            :list="availableLocales"
            :header-data="headerData"
            is-centered
            :route-path="routePath"
            @update:active-item="switchLanguage"
          />
          <TheHeaderSwitcher
            v-if="headerData.isMultiCurrency && hasMultiCurrency"
            show-bottom-sheet
            :list="simplifiedCurrencies"
            :active-item="activeCurrency || ''"
            :header-data="headerData"
            :route-path="routePath"
            @update:active-item="switchCurrency"
          />
        </div>

        <div class="mobile-footer">
          <a
            v-if="headerData.phone"
            class="mobile-call"
            :href="`tel:${headerData.phone}`"
          >
            <icon-phone-in-call />
            <span dir="ltr">{{ headerData.phone }}</span>
          </a>
          <LayoutTheFooterSocialMedias
            v-if="config.public.footer?.socialLinks"
            class="mobile-socials"
            :socials="config.public.footer.socialLinks"
          />
        </div>
      </div>
    </BaseBottomSheet>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useAsyncData, useCookie, useRuntimeConfig } from "nuxt/app";
import type { ICurrency } from "../../../types/general";
import customFetch from "../../../composables/customFetch";
import TheHeaderSwitcher from "./TheHeaderSwitcher.vue";
import { useCartStore } from "../../../stores/cart.store";

defineProps({
  routePath: { type: String, default: "/" },
});
const emit = defineEmits(["open-login"]);

const { t } = useI18n();
const { locale, locales, setLocale } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig() as unknown as RuntimeConfiguration;
const headerData = config.public.header;

const links = computed<any[]>(() => headerData.links ?? []);
// The sheet links straight to the cart rather than nesting the cart's own
// dropdown inside this one.
const cartStore = useCartStore();
const cartCount = computed(() => cartStore.getCartCount ?? 0);
const menuOpen = ref(false);

const { data: currenciesList } = await useAsyncData<ICurrency[]>(
  "currencies",
  () => customFetch(`api/currencies`),
);

const activeLang = computed(() => locale.value || "");
const activeCurrency = useCookie<string>("currency");
const availableLocales = computed(() => locales.value);
const hasMultiLang = computed(() => (locales.value?.length ?? 0) > 1);
const hasMultiCurrency = computed(() => (currenciesList.value?.length ?? 0) > 1);
const simplifiedCurrencies = computed(
  () =>
    currenciesList.value?.map((item) => ({
      name: item?.title,
      code: item.currency_code,
      id: item.pivot.currency_id,
    })) ?? [],
);

const switchLanguage = async (langCode: string) => {
  if (langCode !== locale.value) await setLocale(langCode);
};
const switchCurrency = (newCurrencyCode: string) => {
  if (newCurrencyCode !== activeCurrency.value) {
    activeCurrency.value = newCurrencyCode;
    window.location.reload();
  }
};

// The bar is flat over the top of the page and only earns its shadow once the
// page has moved — the one piece of chrome motion in the design.
// `body` is the scroll container here (main.scss), so `window.scrollY` would
// always read 0 — the whole app listens on `document.body` instead.
const isScrolled = ref(false);
const onScroll = () => {
  isScrolled.value = document.body.scrollTop > 8;
};
onMounted(() => {
  onScroll();
  document.body.addEventListener("scroll", onScroll, { passive: true });
});
onUnmounted(() => document.body.removeEventListener("scroll", onScroll));
</script>

<style lang="scss" scoped>
/* Visibility is done with media queries rather than Tailwind's `lg:` /
   `max-lg:` variants — those classes are not emitted in this project's
   generated stylesheet, so they silently do nothing. */
.desktop-only {
  @media (max-width: 1024px) {
    display: none !important;
  }
}
.menu-control {
  @media (min-width: 1025px) {
    display: none !important;
  }
}
.header-two-eight {
  @apply sticky top-0 z-50 w-full bg-white;
  transition: box-shadow 0.3s ease;

  &.scrolled {
    @apply shadow-1;
  }
}
.header-two-eight-inner {
  @apply flex items-center justify-between gap-5;
  /* Full-bleed background, content capped at the shell width and centred. */
  padding-block: 12px;
  padding-inline: max(80px, calc((100% - var(--shell-max, 1280px)) / 2 + 80px));

  /* Mobile bar: 70px tall, 20px gutters, logo against a single menu control. */
  @media (max-width: 1024px) {
    padding: 14px 20px;
    height: 70px;
  }
}
.logo {
  @apply flex items-center shrink-0;

  img {
    @apply object-contain;
    height: 44px;
    max-width: 228px;

    @media (max-width: 1024px) {
      height: 38px;
      max-width: 163px;
    }
  }
}
.nav-links {
  @apply flex items-center;
  gap: 40px;
}
.nav-link {
  @apply text-primary-1 whitespace-nowrap;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;

  &:hover {
    @apply text-gray-1;
  }
}
/* TheHeaderSwitcher ships no styles of its own — every rule for it lives
   under `.the-header .main-menu .menu .switcher` in styles.scss, which this
   header is not inside. Without them the dropdown is a static block that is
   always open, so the panel is described here instead. */
.header-actions :deep(.switcher) {
  @apply relative;
}
.header-actions :deep(.dropdown-toggle) {
  @apply flex items-center text-primary-1 cursor-pointer whitespace-nowrap;
  font-size: 16px;

  svg {
    @apply text-[12px];
  }
}
.header-actions :deep(.dropdown-menu) {
  @apply absolute bg-white z-50 overflow-hidden;
  top: calc(100% + 12px);
  inset-inline-end: 0;
  min-width: 150px;
  padding: 6px;
  border-radius: 10px;
  border: 1px solid var(--gray-3);
  box-shadow: 0 10px 30px rgba(19, 64, 108, 0.12);
}
.header-actions :deep(.menu-item) {
  @apply flex items-center justify-between gap-3 text-gray-1 cursor-pointer whitespace-nowrap;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 15px;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    @apply bg-gray-4 text-primary-1;
  }
  &.active {
    @apply bg-primary-2-lighter text-primary-1;
    font-weight: 700;
  }

  /* The switcher renders its check on every row and leans on CSS to show it
     only for the active one — styles.scss does this too. Scoped to the
     switcher so the cart's own leading icons are left alone. */
  &:not(.cart-menu-item) svg {
    @apply hidden text-[16px] text-primary-2 shrink-0 mb-0;
  }
  &.active:not(.cart-menu-item) svg {
    @apply block;
  }
}
.header-actions {
  @apply flex items-center shrink-0;
  gap: 20px;

  @media (max-width: 1024px) {
    gap: 12px;
  }
}
.phone-pill {
  @apply flex items-center gap-1 text-gray-1 border border-gray-1;
  padding: 8px 20px;
  border-radius: 10px;
  font-size: 16px;
  line-height: 24px;
  transition:
    background-color 0.3s ease,
    color 0.3s ease,
    border-color 0.3s ease;

  svg {
    @apply text-[24px];
  }

  &:hover {
    @apply bg-primary-1 text-white border-primary-1;
  }
}
/* Outlined square rather than a bare glyph, as drawn. */
.menu-control {
  @apply flex items-center justify-center text-primary-1 border border-gray-3 shrink-0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  font-size: 24px;
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease,
    color 0.3s ease;

  &:active {
    @apply bg-primary-1 text-white border-primary-1;
  }
}
.sheet-logo img {
  @apply object-contain;
  height: 38px;
  max-width: 163px;
}
.mobile-menu {
  @apply flex flex-col;
  /* The sheet body is auto-height, so the column is given the room it needs
     for `mt-auto` to push the footer to the bottom. */
  min-height: calc(100vh - 150px);
  padding: 8px 20px 28px;
  gap: 28px;
}
.mobile-nav {
  @apply flex flex-col;
}
.mobile-link {
  @apply flex items-center justify-between text-primary-1 border-b border-gray-3;
  padding: 18px 0;
  font-size: 20px;
  font-weight: 700;
  transition: color 0.3s ease;

  &:active {
    @apply text-primary-2;
  }
  &:active .mobile-link-arrow {
    transform: translateX(4px);
  }
}
.mobile-link-cart {
  @apply flex items-center;
  gap: 10px;
}
.cart-count {
  @apply flex items-center justify-center bg-primary-2 text-white rounded-full;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  font-size: 14px;
  font-weight: 700;
}
.mobile-link-arrow {
  @apply text-gray-2 text-[16px] shrink-0;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  :deep(path) {
    stroke: currentColor;
  }
}
/* Each preference is its own outlined chip, matching the menu control in the
   bar above. */
.mobile-switchers {
  @apply flex items-center;
  gap: 12px;

  :deep(.switcher) {
    @apply border border-gray-3;
    padding: 6px 12px;
    border-radius: 10px;
  }
  :deep(.dropdown-toggle) {
    @apply text-primary-1;
    font-size: 15px;
    font-weight: 600;
  }
}
/* Pinned to the bottom of the sheet: the one action plus the social row. */
.mobile-footer {
  @apply flex flex-col mt-auto;
  gap: 20px;
}
.mobile-call {
  @apply flex items-center justify-center bg-primary-2 text-white w-full;
  gap: 10px;
  height: 56px;
  border-radius: 28px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.32px;

  svg {
    @apply text-[20px];
  }
}
/* The shared social row paints itself in the footer's text colour (white for
   this client), which would vanish on the sheet's white ground. */
.mobile-socials :deep(svg) {
  color: var(--primary-1) !important;
}
</style>
